import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AREA_UTIL,
  areaUtilNaCelula,
  LAYOUTS,
  LISTA_DE_LAYOUTS,
  temEspacoDeMarcador,
  type Camada,
  type FonteDaFoto,
  type LayoutId,
} from "@foto/shared";
import { trpc } from "../../lib/trpc.js";
import { montarFolhaDeImpressao } from "../../lib/api.js";
import { useAuth } from "../auth/useAuth.js";
import { Pagina } from "../../components/Pagina.js";
import { Sobreposicao } from "../../components/Sobreposicao.js";
import { ImagemAmpliavel } from "../../components/Ampliar.js";
import { VisorDeImagem } from "../../components/VisorDeImagem.js";
import { AREA_UTIL_DA_FOLHA, TAMANHO_DA_FOLHA } from "../../components/visor.js";
import { Botao, Falha, Girando, Segmentos, Vazio, cx } from "../../components/ui.js";
import { EditorDeEnquadramento, emMm } from "./EditorDeEnquadramento.js";
import {
  EditorDeCamadas,
  type AplicacaoRecusada,
  type ResultadoDaAplicacao,
} from "../camadas/EditorDeCamadas.js";
import { motivoDaAplicacaoNaoCaber } from "../camadas/limites-da-folha.js";
import { useFigurinhasDasCamadas } from "../camadas/useFigurinhasDasCamadas.js";
import { MiniaturaDoFormato, PreviewDaFolha, type MarcadorPronto } from "./PreviewDaFolha.js";
import { SeletorDeMarcador } from "./SeletorDeMarcador.js";
import { ChaveDeVersaoDaFoto, FiltrosDasFotos, PaginacaoDasFotos } from "./FiltrosDasFotos.js";
import { GradeDeFotos } from "./GradeDeFotos.js";
import { VisorDaFoto } from "./VisorDaFoto.js";
import { MINIMO_DA_GRADE, fonteDoVisor, rotuloDeColocar, versoesDaFoto } from "./ampliar-fotos.js";
import { FilaDeImpressao } from "./FilaDeImpressao.js";
import { PainelDeImpressoras } from "./PainelDeImpressoras.js";
import { PainelImprimir } from "./PainelImprimir.js";
import { pedeOPainelDeImpressoras } from "./alvo.js";
import { algumaPodeServir } from "./impressora.js";
import { useFotosPublicadas } from "./useFotosPublicadas.js";
import { tamanhoDaFoto } from "./tamanho-da-foto.js";
import {
  baixarArquivo,
  compartilharArquivo,
  nomeDaFolha,
  podeCompartilharArquivo,
} from "./entrega.js";
import {
  aplicarCamadasEmTodas,
  celulasComImagemIndisponivel,
  chaveDaFoto,
  colocarFoto,
  colocarMarcador,
  distribuir,
  ehEspacoDeMarcador,
  esvaziarCelula,
  folhaVazia,
  fonteParaColocar,
  guardarCamadas,
  telaDaVersao,
  guardarEnquadramento,
  idsDeFigurinhasDaFolha,
  pedidoDaFolha,
  proximaCelulaVazia,
  quantasCamadas,
  quantasFotos,
  quantoTemNaFolha,
  repetirEmTodas,
  tamanhoDaCelula,
  tamanhoMostrado,
  telaDaCelula,
  trocarFonte,
  trocarLayout,
  usarAmpliada,
  voltarAOriginal,
  type Folha,
  type Tamanhos,
} from "./folhas.js";

/**
 * Impressão: monta a folha da SELPHY (postal 100 × 148 mm) com as fotos
 * publicadas, enquadra cada uma e entrega o JPEG.
 *
 * A prévia é feita aqui, em escala, para montar sem esperar o servidor a cada
 * toque. O arquivo de verdade — o que se baixa e se compartilha — é montado no
 * servidor com a mesma geometria (`@foto/shared`), e "Ver como vai sair" mostra
 * esse arquivo antes de gastar uma folha de papel.
 *
 * Há dois caminhos até o papel: "Imprimir" manda a folha à impressora pelo
 * servidor (que só consegue se alcançar a impressora, na rede da casa), e
 * "Baixar"/"Compartilhar" levam o JPEG ao app da Canon, que sempre funciona. Os
 * dois ficam à mão: quando o primeiro não alcança a impressora, o segundo é a
 * saída, e a tela não esconde isso.
 */
/**
 * Quantas fotos a bandeja mostra de cada vez (#103): poucas o bastante para caber ao lado da folha sem rolar
 * muito, com páginas para as demais.
 */
const FOTOS_POR_PAGINA = 8;

export function ImpressaoPage() {
  const [parametros] = useSearchParams();
  const pedidas = useMemo(
    () =>
      (parametros.get("fotos") ?? "")
        .split(",")
        .map(Number)
        .filter((n) => Number.isInteger(n) && n > 0),
    [parametros],
  );

  const bandeja = useFotosPublicadas(pedidas, { porPagina: FOTOS_POR_PAGINA });
  const secaoDaBandeja = useRef<HTMLElement>(null);
  // Trocar de página com o topo da grade fora da tela devolve a grade à vista: no celular a paginação fica embaixo,
  // e a página nova começaria escondida acima.
  const mostrarABandeja = useCallback(() => {
    const secao = secaoDaBandeja.current;
    if (!secao || secao.getBoundingClientRect().top >= 0) return;
    const reduzido = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    secao.scrollIntoView({ behavior: reduzido ? "auto" : "smooth", block: "start" });
  }, []);
  // As fotos já vistas, e não só as da lista de agora: a prévia precisa das que estão
  // nos espaços mesmo quando um filtro as tira da bandeja.
  const fotos = bandeja.vistas;
  // A versão que as miniaturas mostram e que um toque coloca.
  const versao = bandeja.versao;

  const [folhas, setFolhas] = useState<Folha[]>([]);
  const [atualId, setAtualId] = useState(1);
  const [selecionada, setSelecionada] = useState<number | null>(null);
  /** O espaço de marcador cujo seletor está aberto (#115). */
  const [escolhendoMarcador, setEscolhendoMarcador] = useState<number | null>(null);
  const [tamanhos, setTamanhos] = useState<Tamanhos>({});
  const tamanhosRef = useRef<Tamanhos>({});
  const proximoId = useRef(2);
  const iniciou = useRef(false);

  const [editando, setEditando] = useState<number | null>(null);
  // O editor de figurinhas, logos e textos, aberto sobre este espaço da folha.
  const [editandoCamadas, setEditandoCamadas] = useState<number | null>(null);
  const [vendo, setVendo] = useState<{ url: string; blob: Blob; nome: string } | null>(null);
  // A imagem de "Ver como vai sair" em tela cheia, para conferir a borda e a área útil do papel.
  const [ampliandoFolha, setAmpliandoFolha] = useState(false);
  // A foto da bandeja aberta no visor, com as duas versões.
  const [ampliando, setAmpliando] = useState<number | null>(null);
  const [ocupado, setOcupado] = useState<"ver" | "baixar" | "compartilhar" | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [podeCompartilhar] = useState(podeCompartilharArquivo);

  // Impressão direta. `impressoras` aqui só decide qual botão é o principal; o painel faz a própria
  // consulta, sempre nova, ao abrir.
  const { ehAdmin } = useAuth();
  // Quem não achou impressora em Marcadores chega aqui com o painel de impressoras já aberto.
  const [painel, setPainel] = useState<"imprimir" | "impressoras" | null>(() =>
    ehAdmin && pedeOPainelDeImpressoras(parametros) ? "impressoras" : null,
  );
  const impressoras = trpc.impressao.impressoras.useQuery();
  // Com uma impressora que o sistema pode alcançar, "Imprimir" é o caminho principal; sem nenhuma (ou
  // com todas fora de alcance), o principal continua sendo levar o JPEG ao app da Canon.
  const impressaoDireta = impressoras.data ? algumaPodeServir(impressoras.data) : false;
  const fecharPainel = useCallback(() => setPainel(null), []);

  // A IA mora aqui, e não no editor: a ampliação leva uns 40 s e custa uma foto do
  // pacote, e o resultado não pode se perder se o operador fechar o editor no meio.
  const utils = trpc.useUtils();
  const saldo = trpc.lotes.saldo.useQuery();
  const ajustar = trpc.impressao.ajustarComIa.useMutation();
  const ampliar = trpc.impressao.ampliarCena.useMutation();
  const [mensagemIa, setMensagemIa] = useState<string | null>(null);
  // Cresce a cada resultado de IA: faz o editor aberto recarregar com a imagem nova.
  const [versaoDoEditor, setVersaoDoEditor] = useState(0);

  /**
   * Lê o tamanho das imagens que ainda não se conhece — por foto e versão, que têm
   * proporções diferentes —, e devolve o conjunto todo.
   */
  async function carregarTamanhos(
    pares: { id: number; fonte?: FonteDaFoto | undefined }[],
  ): Promise<Tamanhos> {
    const novos: Tamanhos = {};
    await Promise.all(
      pares.map(async ({ id, fonte }) => {
        const foto = fotos.get(id);
        const chave = chaveDaFoto(id, fonte);
        if (!foto || tamanhosRef.current[chave]) return;
        try {
          novos[chave] = await tamanhoDaFoto(telaDaVersao(foto, fonte));
        } catch {
          // Sem o tamanho, a foto fica na prévia padrão e o servidor mede o original.
        }
      }),
    );
    const todos = { ...tamanhosRef.current, ...novos };
    tamanhosRef.current = todos;
    setTamanhos(todos);
    return todos;
  }

  // Monta as folhas de partida quando as fotos chegam: as pedidas pela
  // Biblioteca, repartidas no formato que serve a quantidade.
  useEffect(() => {
    if (iniciou.current || !bandeja.consulta.data) return;
    iniciou.current = true;

    const disponiveis = pedidas.filter((id) => fotos.has(id));
    if (disponiveis.length === 0) {
      setFolhas([folhaVazia(1, "quatro")]);
      return;
    }
    void carregarTamanhos(disponiveis.map((id) => ({ id }))).then((todos) => {
      const montadas = distribuir(disponiveis, todos, 1);
      proximoId.current = montadas.length + 1;
      setFolhas(montadas);
      setAtualId(1);
    });
    // Só na primeira chegada das fotos.
  }, [bandeja.consulta.data]);

  const folha = folhas.find((f) => f.id === atualId) ?? folhas[0];

  // O que a biblioteca diz das figurinhas desta folha: a prévia as desenha, e a tela avisa se alguma sumiu.
  const idsDasFigurinhas = useMemo(() => (folha ? idsDeFigurinhasDaFolha(folha) : []), [folha]);
  const figurinhas = useFigurinhasDasCamadas(idsDasFigurinhas);

  // Os marcadores prontos, só quando o formato tem espaço para eles (#115): a prévia recorta a faixa da folha deles.
  const comMarcador = folha ? temEspacoDeMarcador(folha.layout) : false;
  const listaDeMarcadores = trpc.marcadores.list.useQuery(undefined, {
    enabled: comMarcador,
    staleTime: 60_000,
  });
  const marcadoresProntos = useMemo(
    () =>
      new Map<number, MarcadorPronto>(
        (listaDeMarcadores.data ?? []).map((m) => [m.id, { titulo: m.titulo, tela: m.folha.tela }]),
      ),
    [listaDeMarcadores.data],
  );

  function mudarFolha(nova: Folha) {
    setFolhas((fs) => fs.map((f) => (f.id === nova.id ? nova : f)));
  }

  function escolherFormato(layout: LayoutId) {
    if (!folha) return;
    mudarFolha(trocarLayout(folha, layout, tamanhosRef.current));
    setSelecionada(null);
    setAviso(null);
  }

  /**
   * Coloca a foto no espaço escolhido, ou no próximo vazio. A versão é a da bandeja — a que as
   * miniaturas mostram —, salvo quando o visor pede a que estava à vista nele.
   */
  async function colocar(fotoId: number, desejada: FonteDaFoto = versao) {
    if (!folha) return;
    // A foto vai num espaço de foto: com um espaço de marcador escolhido, vai no próximo vazio.
    const alvo =
      selecionada !== null && !ehEspacoDeMarcador(folha.layout, selecionada)
        ? selecionada
        : proximaCelulaVazia(folha);
    if (alvo === null) {
      setAviso("A folha está cheia: toque num espaço da folha para trocar a foto dele.");
      return;
    }
    setAviso(null);
    // A foto da IA e a original só entram quando a foto as tem; sem elas, a peça.
    const fonte = fonteParaColocar(fotos.get(fotoId), desejada);
    const todos = await carregarTamanhos([{ id: fotoId, fonte }]);
    const nova = colocarFoto(folha, alvo, fotoId, todos, fonte);
    mudarFolha(nova);
    setSelecionada(proximaCelulaVazia(nova, alvo + 1));
  }

  /** Troca a versão da foto de um espaço já colocado (peça com capa, foto da IA ou original). */
  async function trocarVersao(indice: number, fonte: FonteDaFoto | undefined) {
    if (!folha) return;
    const alvo = folha;
    const c = alvo.celulas[indice];
    if (!c) return;
    const todos = await carregarTamanhos([{ id: c.fotoId, fonte }]);
    setMensagemIa(null);
    setFolhas((fs) => fs.map((f) => (f.id === alvo.id ? trocarFonte(f, indice, fonte, todos) : f)));
    setVersaoDoEditor((v) => v + 1);
  }

  function adicionarFolha() {
    const id = proximoId.current++;
    setFolhas((fs) => [...fs, folhaVazia(id, folha?.layout ?? "quatro")]);
    setAtualId(id);
    setSelecionada(null);
  }

  function removerFolha() {
    if (!folha || folhas.length < 2) return;
    const resto = folhas.filter((f) => f.id !== folha.id);
    setFolhas(resto);
    setAtualId(resto[0]!.id);
    setSelecionada(null);
  }

  /** Ajusta a célula às pessoas. Só vale se a célula ainda é a mesma da chamada. */
  function ajustarComIa(indice: number) {
    if (!folha) return;
    const alvo = folha;
    const c = alvo.celulas[indice];
    if (!c) return;

    setMensagemIa(null);
    ajustar.mutate(
      {
        geracaoId: c.fotoId,
        ...(c.fonte ? { fonte: c.fonte } : {}),
        celula: tamanhoDaCelula(alvo.layout, indice),
      },
      {
        onSuccess: (r) => {
          setFolhas((fs) =>
            fs.map((f) => {
              const atual = f.celulas[indice];
              const igual = f.id === alvo.id && atual?.fotoId === c.fotoId && !atual.ampliada;
              return igual ? guardarEnquadramento(f, indice, r.enquadramento) : f;
            }),
          );
          setVersaoDoEditor((v) => v + 1);
          setMensagemIa(
            r.semPessoas
              ? "Não achei pessoas nesta foto: deixei o espaço preenchido, e nada foi cobrado."
              : r.faltaCenario
                ? "As pessoas não cabem neste espaço sem margem branca: a foto não tem cenário suficiente. Use “Ampliar a cena com IA”."
                : `Ajustei para manter ${r.pessoas === 1 ? "a pessoa inteira" : `as ${r.pessoas} pessoas inteiras`}.`,
          );
          void utils.lotes.saldo.invalidate();
        },
        onError: (e) => setMensagemIa(e.message),
      },
    );
  }

  /** Amplia o cenário da foto da célula e passa a usar a imagem nova nela. */
  function ampliarComIa(indice: number) {
    if (!folha) return;
    const alvo = folha;
    const c = alvo.celulas[indice];
    if (!c) return;

    setMensagemIa(null);
    ampliar.mutate(
      {
        geracaoId: c.fotoId,
        ...(c.fonte ? { fonte: c.fonte } : {}),
        celula: tamanhoDaCelula(alvo.layout, indice),
      },
      {
        onSuccess: (r) => {
          if (!r.ampliada) {
            setMensagemIa(r.mensagem);
            return;
          }
          setFolhas((fs) =>
            fs.map((f) =>
              f.id === alvo.id && f.celulas[indice]?.fotoId === c.fotoId
                ? usarAmpliada(f, indice, {
                    id: r.imagemId,
                    tela: r.tela,
                    tamanho: r.tamanho,
                    aviso: r.aviso,
                  })
                : f,
            ),
          );
          setVersaoDoEditor((v) => v + 1);
          setMensagemIa(
            r.suspeito ? r.aviso : "Cenário ampliado. As pessoas ficam como estavam na foto.",
          );
          void utils.lotes.saldo.invalidate();
        },
        onError: (e) => setMensagemIa(e.message),
      },
    );
  }

  /** O rótulo de uma foto da folha, para dizer de qual se fala: o nome e o espaço dela. */
  function rotuloDoEspaco(alvo: Folha, indice: number): string {
    const c = alvo.celulas[indice];
    const nome = c ? fotos.get(c.fotoId)?.nome : undefined;
    return `${nome ?? "Foto"} (espaço ${indice + 1})`;
  }

  /**
   * "Aplicar em todas as fotos", feito no editor de camadas: copia as camadas do espaço para as outras fotos da
   * folha. Antes de aplicar, confere os tetos que o servidor valida (60 camadas e 24 figurinhas diferentes por
   * folha): passando deles, nada muda e o editor mostra o porquê.
   */
  function aplicarCamadasNasOutras(
    indice: number,
    camadas: Camada[],
    modo: "substituir" | "acrescentar",
  ): ResultadoDaAplicacao | AplicacaoRecusada {
    if (!folha) return { recusada: "Não há folha aberta." };
    const antes = folha;
    const aplicada = aplicarCamadasEmTodas(guardarCamadas(antes, indice, camadas), indice, modo);
    const motivo = motivoDaAplicacaoNaoCaber(
      aplicada.folha.celulas.map((c) => (c ? (c.camadas ?? []) : null)),
    );
    if (motivo) return { recusada: motivo };

    mudarFolha(aplicada.folha);
    return {
      atualizadas: aplicada.atualizadas.length,
      naoCouberam: aplicada.naoCouberam.map((i) => rotuloDoEspaco(antes, i)),
      desfazer: () => setFolhas((fs) => fs.map((f) => (f.id === antes.id ? antes : f))),
    };
  }

  /** O JPEG, montado no servidor. */
  async function gerar(): Promise<{ blob: Blob; nome: string } | null> {
    if (!folha) return null;
    setErro(null);
    try {
      const blob = await montarFolhaDeImpressao(pedidoDaFolha(folha));
      return { blob, nome: nomeDaFolha(folha.layout) };
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível montar a folha");
      return null;
    }
  }

  async function verComoSai() {
    setOcupado("ver");
    const r = await gerar();
    setOcupado(null);
    if (r) setVendo({ ...r, url: URL.createObjectURL(r.blob) });
  }

  function fecharVisor() {
    if (vendo) URL.revokeObjectURL(vendo.url);
    setAmpliandoFolha(false);
    setVendo(null);
  }

  async function baixar() {
    setOcupado("baixar");
    const r = await gerar();
    setOcupado(null);
    if (r) baixarArquivo(r.blob, r.nome);
  }

  async function compartilhar() {
    setOcupado("compartilhar");
    const r = await gerar();
    try {
      if (r) await compartilharArquivo(r.blob, r.nome);
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível compartilhar");
    }
    setOcupado(null);
  }

  if (bandeja.consulta.isLoading || (!folha && !bandeja.consulta.isError)) {
    return (
      <Pagina titulo="Impressão">
        <Girando className="mx-auto my-12 size-6 text-texto-fraco" />
      </Pagina>
    );
  }
  if (bandeja.consulta.isError && !bandeja.consulta.data) {
    return (
      <Pagina titulo="Impressão">
        <Falha
          mensagem={bandeja.consulta.error.message}
          tentarNovamente={() => void bandeja.consulta.refetch()}
        />
      </Pagina>
    );
  }
  if (!folha) return null;

  const naCelula = selecionada !== null ? folha.celulas[selecionada] : null;
  const fotoDaCelula = naCelula ? fotos.get(naCelula.fotoId) : undefined;
  // As versões que a foto do espaço escolhido tem: a peça, e a da IA e a original quando existem à parte.
  const versoesDoEspaco = fotoDaCelula ? versoesDaFoto(fotoDaCelula, { comOriginal: true }) : [];
  const tamanhoDaFotoEditada = naCelula ? tamanhoMostrado(naCelula, tamanhos) : undefined;
  // "Sem nada": nem foto, nem marcador. A folha mista só com marcadores também imprime.
  const semFotos = quantoTemNaFolha(folha) === 0;
  const espacoDeMarcador = selecionada !== null && ehEspacoDeMarcador(folha.layout, selecionada);
  const marcadorNoEspaco = selecionada !== null ? folha.marcadores?.[selecionada] : undefined;
  // O servidor recusa o pedido da folha inteira enquanto houver uma figurinha que não existe mais.
  const comImagemIndisponivel = celulasComImagemIndisponivel(folha, figurinhas);
  const usos = new Map<number, number>();
  for (const c of folha.celulas) if (c) usos.set(c.fotoId, (usos.get(c.fotoId) ?? 0) + 1);

  return (
    <Pagina
      titulo="Impressão"
      descricao="Monte a folha da SELPHY com as fotos publicadas, enquadre cada uma, acrescente figurinhas e textos e imprima, ou gere o JPEG para o app da Canon."
      acao={
        ehAdmin ? (
          <Botao tom="quieto" onClick={() => setPainel("impressoras")}>
            Impressoras
          </Botao>
        ) : undefined
      }
    >
      {bandeja.itens.length === 0 && !bandeja.filtrando ? (
        <Vazio
          titulo="Nenhuma foto publicada"
          descricao="As fotos aprovadas e publicadas aparecem aqui para imprimir."
        />
      ) : (
        <div className="grid gap-8 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
          <section aria-label="A folha" className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              {folhas.map((f, i) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => {
                    setAtualId(f.id);
                    setSelecionada(null);
                  }}
                  aria-pressed={f.id === folha.id}
                  className={cx(
                    "min-h-11 rounded-peca border px-3 text-[0.875rem]",
                    f.id === folha.id
                      ? "border-brasa bg-brasa-fraca text-brasa"
                      : "border-borda bg-superficie text-texto-suave hover:border-borda-forte",
                  )}
                >
                  Folha {i + 1}
                </button>
              ))}
              <Botao tom="fantasma" formato="nu" onClick={adicionarFolha}>
                + Folha
              </Botao>
              {folhas.length > 1 && (
                <Botao tom="fantasma" formato="nu" onClick={removerFolha}>
                  Remover esta
                </Botao>
              )}
            </div>

            <div role="group" aria-label="Formato da folha" className="grid grid-cols-3 gap-2">
              {LISTA_DE_LAYOUTS.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => escolherFormato(l.id)}
                  aria-pressed={folha.layout === l.id}
                  title={l.descricao}
                  className={cx(
                    "flex flex-col items-center gap-2 rounded-peca border px-2 py-3 text-center text-[0.8125rem]",
                    folha.layout === l.id
                      ? "border-brasa bg-brasa-fraca text-brasa"
                      : "border-borda bg-superficie text-texto-suave hover:border-borda-forte",
                  )}
                >
                  <MiniaturaDoFormato layout={l.id} />
                  {l.nome}
                </button>
              ))}
            </div>

            <div className="mx-auto w-full max-w-[22rem]">
              <PreviewDaFolha
                folha={folha}
                fotos={fotos}
                tamanhos={tamanhos}
                figurinhas={figurinhas}
                selecionada={selecionada}
                aoEscolher={(i) => setSelecionada(i === selecionada ? null : i)}
                marcadoresProntos={marcadoresProntos}
              />
            </div>

            <p className="text-[0.8125rem] leading-relaxed text-texto-fraco">
              {LAYOUTS[folha.layout].descricao}{" "}
              {selecionada === null
                ? "Toque num espaço da folha para escolher a foto dele ou ajustar o enquadramento."
                : espacoDeMarcador
                  ? "Espaço de marcador: escolha um dos marcadores já gerados."
                  : naCelula
                    ? "Espaço escolhido: ajuste o enquadramento ou troque a foto."
                    : "Espaço escolhido: toque numa foto ao lado para colocá-la aqui."}
            </p>
            <p className="text-[0.75rem] leading-relaxed text-texto-fraco">
              A linha azul tracejada é a área que o papel imprime e mantém (
              {emMm(AREA_UTIL.larguraMm)} × {emMm(AREA_UTIL.alturaMm)} mm): o que passa dela cai no
              picotado. A foto cheia vai até a borda de propósito.
            </p>

            {espacoDeMarcador && selecionada !== null && (
              <div className="flex flex-wrap gap-2">
                <Botao tom="quieto" onClick={() => setEscolhendoMarcador(selecionada)}>
                  {marcadorNoEspaco ? "Trocar o marcador" : "Escolher marcador"}
                </Botao>
                {marcadorNoEspaco && (
                  <Botao
                    tom="fantasma"
                    formato="nu"
                    onClick={() => mudarFolha(esvaziarCelula(folha, selecionada))}
                  >
                    Tirar o marcador
                  </Botao>
                )}
              </div>
            )}

            {naCelula && selecionada !== null && (
              <div className="flex flex-wrap gap-2">
                <Botao
                  tom="quieto"
                  disabled={!tamanhoDaFotoEditada}
                  onClick={() => {
                    setMensagemIa(null);
                    setEditando(selecionada);
                  }}
                >
                  Enquadrar
                </Botao>
                <Botao
                  tom="quieto"
                  disabled={!tamanhoDaFotoEditada}
                  onClick={() => setEditandoCamadas(selecionada)}
                >
                  {quantasCamadas(naCelula) > 0
                    ? `Figurinhas e textos (${quantasCamadas(naCelula)})`
                    : "Figurinhas e textos"}
                </Botao>
                {folha.celulas.length > 1 && (
                  <Botao
                    tom="quieto"
                    onClick={() => mudarFolha(repetirEmTodas(folha, selecionada))}
                  >
                    Repetir em todos os espaços
                  </Botao>
                )}
                <Botao
                  tom="fantasma"
                  formato="nu"
                  onClick={() => mudarFolha(esvaziarCelula(folha, selecionada))}
                >
                  Tirar a foto
                </Botao>
              </div>
            )}

            {naCelula && selecionada !== null && versoesDoEspaco.length > 1 && (
              // As versões que esta foto tem: trocar volta o enquadramento ao padrão, como trocar de foto.
              <div className="flex flex-col gap-1.5">
                {/* Visível, e não só para o leitor de tela: a chave da bandeja tem as mesmas posições, e esta
                    vale só para o espaço escolhido. */}
                <p className="text-[0.8125rem] text-texto-suave">
                  Versão desta foto, só no espaço {selecionada + 1}
                </p>
                <Segmentos
                  rotulo={`Versão desta foto, só no espaço ${selecionada + 1}`}
                  valor={naCelula.fonte ?? "peca"}
                  opcoes={versoesDoEspaco.map((v) => ({
                    id: fonteDoVisor(v.id),
                    rotulo: v.rotulo,
                  }))}
                  aoMudar={(v) => void trocarVersao(selecionada, v === "peca" ? undefined : v)}
                  className="self-start"
                />
              </div>
            )}

            {naCelula?.ampliada?.aviso && (
              <p className="text-[0.8125rem] text-texto-suave">{naCelula.ampliada.aviso}</p>
            )}
            {aviso && <p className="text-[0.8125rem] text-texto-suave">{aviso}</p>}
            {comImagemIndisponivel.length > 0 && (
              <Falha
                mensagem={`Esta folha tem figurinha que não existe mais (foi apagada ou deixou de ser pública), em: ${comImagemIndisponivel
                  .map((i) => rotuloDoEspaco(folha, i))
                  .join(
                    ", ",
                  )}. Enquanto ela estiver na folha, o pedido falha. Abra "Figurinhas e textos" no espaço e remova a imagem.`}
              />
            )}
            {erro && <Falha mensagem={erro} />}

            <div className="flex flex-wrap gap-2 border-t border-borda pt-4">
              <Botao
                className="min-h-11"
                tom={impressaoDireta ? "brasa" : "quieto"}
                disabled={semFotos || ocupado !== null}
                onClick={() => setPainel("imprimir")}
              >
                Imprimir
              </Botao>
              <Botao
                className="min-h-11"
                tom={impressaoDireta ? "quieto" : "brasa"}
                disabled={semFotos || ocupado !== null}
                carregando={ocupado === "baixar"}
                onClick={() => void baixar()}
              >
                Baixar JPEG
              </Botao>
              {podeCompartilhar && (
                <Botao
                  className="min-h-11"
                  tom="quieto"
                  disabled={semFotos || ocupado !== null}
                  carregando={ocupado === "compartilhar"}
                  onClick={() => void compartilhar()}
                >
                  Compartilhar com o app
                </Botao>
              )}
              <Botao
                className="min-h-11"
                tom="quieto"
                disabled={semFotos || ocupado !== null}
                carregando={ocupado === "ver"}
                onClick={() => void verComoSai()}
              >
                Ver como vai sair
              </Botao>
            </div>

            <FilaDeImpressao />
          </section>

          <section ref={secaoDaBandeja} aria-label="Fotos publicadas" className="scroll-mt-4">
            <h2 className="mb-1 text-[0.9375rem] font-medium text-texto">Fotos publicadas</h2>
            <p className="mb-3 text-[0.8125rem] text-texto-suave">
              Toque numa foto para colocá-la no espaço escolhido, ou no próximo espaço vazio. A lupa
              amplia a foto sem colocá-la.
            </p>

            <FiltrosDasFotos bandeja={bandeja} />

            <ChaveDeVersaoDaFoto
              bandeja={bandeja}
              comOriginal
              legendaPeca="A peça publicada, com a capa da vivência."
              legendaFoto="A imagem que a IA gerou, sem a capa. As fotos sem capa e as salvas sem IA seguem como estão."
              legendaOriginal="A foto que o operador tirou, sem a IA e sem a capa. As fotos salvas sem IA seguem como estão."
            />

            {bandeja.consulta.isError && (
              <Falha
                mensagem={bandeja.consulta.error.message}
                tentarNovamente={() => void bandeja.consulta.refetch()}
              />
            )}
            {bandeja.itens.length === 0 ? (
              <p className="py-6 text-center text-[0.875rem] text-texto-suave">
                {bandeja.consulta.isFetching
                  ? "Buscando…"
                  : "Nada com esses filtros. Tente outra grafia, ou limpe os filtros."}
              </p>
            ) : (
              <GradeDeFotos
                fotos={bandeja.itens}
                versao={versao}
                minimo={MINIMO_DA_GRADE.bandeja}
                usos={usos}
                rotuloDoToque={(f) => rotuloDeColocar(f.nome, usos.get(f.id))}
                aoEscolher={(f) => void colocar(f.id)}
                aoAmpliar={(f) => setAmpliando(f.id)}
              />
            )}
            <PaginacaoDasFotos bandeja={bandeja} aoMudar={mostrarABandeja} />
          </section>
        </div>
      )}

      {escolhendoMarcador !== null && (
        <Sobreposicao rotulo="Escolher o marcador" aoFechar={() => setEscolhendoMarcador(null)}>
          <SeletorDeMarcador
            espaco={escolhendoMarcador + 1}
            aoFechar={() => setEscolhendoMarcador(null)}
            aoEscolher={(m) => {
              mudarFolha(colocarMarcador(folha, escolhendoMarcador, m));
              setEscolhendoMarcador(null);
            }}
          />
        </Sobreposicao>
      )}

      {editando !== null && naCelula && fotoDaCelula && tamanhoDaFotoEditada && (
        <Sobreposicao rotulo="Enquadrar a foto" aoFechar={() => setEditando(null)}>
          <EditorDeEnquadramento
            // Outra foto ou outro espaço é outro editor, sem estado de gesto herdado.
            key={`${folha.id}-${editando}-${naCelula.fotoId}-${naCelula.fonte ?? "peca"}-${naCelula.ampliada?.id ?? 0}-${versaoDoEditor}`}
            fotoUrl={telaDaCelula(naCelula, fotoDaCelula)!}
            foto={tamanhoDaFotoEditada}
            celula={tamanhoDaCelula(folha.layout, editando)}
            areaUtil={areaUtilNaCelula(folha.layout, editando)}
            valor={naCelula.enquadramento}
            ia={{
              ocupado: ajustar.isPending ? "ajustar" : ampliar.isPending ? "ampliar" : null,
              restantes: saldo.data?.restantes,
              ampliada: naCelula.ampliada !== undefined,
              mensagem: mensagemIa,
              aoAjustar: () => ajustarComIa(editando),
              aoAmpliar: () => ampliarComIa(editando),
              aoVoltarAOriginal: () => {
                mudarFolha(voltarAOriginal(folha, editando, tamanhosRef.current));
                setVersaoDoEditor((v) => v + 1);
                setMensagemIa(null);
              },
            }}
            aoSalvar={(e) => {
              mudarFolha(guardarEnquadramento(folha, editando, e));
              setEditando(null);
            }}
            aoCancelar={() => setEditando(null)}
          />
        </Sobreposicao>
      )}

      {editandoCamadas !== null &&
        folha.celulas[editandoCamadas] &&
        fotos.get(folha.celulas[editandoCamadas]!.fotoId) &&
        tamanhoMostrado(folha.celulas[editandoCamadas]!, tamanhos) && (
          <EditorDeCamadas
            // Outro espaço é outro editor. A foto NÃO entra na chave: uma ampliação de IA que chega com o editor
            // aberto troca a imagem de fundo, mas não pode jogar fora o que o operador já arrastou.
            key={`camadas-${folha.id}-${editandoCamadas}`}
            fotoUrl={telaDaCelula(
              folha.celulas[editandoCamadas]!,
              fotos.get(folha.celulas[editandoCamadas]!.fotoId),
            )!}
            foto={tamanhoMostrado(folha.celulas[editandoCamadas]!, tamanhos)!}
            celula={tamanhoDaCelula(folha.layout, editandoCamadas)}
            areaUtil={areaUtilNaCelula(folha.layout, editandoCamadas)}
            enquadramento={folha.celulas[editandoCamadas]!.enquadramento}
            camadas={folha.celulas[editandoCamadas]!.camadas ?? []}
            fotosNaFolha={quantasFotos(folha) - 1}
            camadasDasOutras={folha.celulas.flatMap((c, i) =>
              i === editandoCamadas ? [] : (c?.camadas ?? []),
            )}
            aoSalvar={(camadas) => {
              mudarFolha(guardarCamadas(folha, editandoCamadas, camadas));
              setEditandoCamadas(null);
            }}
            aoCancelar={() => setEditandoCamadas(null)}
            aoAplicarEmTodas={(camadas, modo) =>
              aplicarCamadasNasOutras(editandoCamadas, camadas, modo)
            }
          />
        )}

      {ampliando !== null && fotos.get(ampliando) && (
        <VisorDaFoto
          foto={fotos.get(ampliando)!}
          versao={versao}
          comOriginal
          acao={{
            rotulo: "Colocar na folha",
            aoAcionar: (v) => void colocar(ampliando, v),
          }}
          aoFechar={() => setAmpliando(null)}
        />
      )}

      {vendo && (
        <Sobreposicao rotulo="Como vai sair" aoFechar={fecharVisor}>
          <div className="flex flex-col gap-4 p-4">
            {/* O JPEG montado pelo servidor, ampliável: é o que confere a borda e o picotado. */}
            <ImagemAmpliavel
              rotulo="Ampliar a folha como será impressa"
              aoAmpliar={() => setAmpliandoFolha(true)}
              className="mx-auto"
            >
              <img
                src={vendo.url}
                alt="A folha como será impressa"
                className="block max-h-[65dvh] w-auto max-w-full rounded-peca bg-white"
              />
            </ImagemAmpliavel>
            <p className="text-[0.8125rem] leading-relaxed text-texto-fraco">
              Este é o arquivo que será baixado: 1181 × 1748 px, o postal de 100 × 148 mm a 300 dpi.
            </p>
            <div className="flex flex-wrap justify-end gap-2 border-t border-borda pt-4">
              <Botao tom="quieto" onClick={fecharVisor}>
                Voltar
              </Botao>
              {podeCompartilhar && (
                <Botao
                  tom="quieto"
                  onClick={() =>
                    void compartilharArquivo(vendo.blob, vendo.nome).catch((e: unknown) =>
                      setErro(e instanceof Error ? e.message : "Não foi possível compartilhar"),
                    )
                  }
                >
                  Compartilhar com o app
                </Botao>
              )}
              <Botao
                tom={impressaoDireta ? "quieto" : "brasa"}
                onClick={() => baixarArquivo(vendo.blob, vendo.nome)}
              >
                Baixar JPEG
              </Botao>
              <Botao
                tom={impressaoDireta ? "brasa" : "quieto"}
                onClick={() => {
                  // Um painel por vez: o visor fecha, e o de imprimir abre no lugar dele.
                  fecharVisor();
                  setPainel("imprimir");
                }}
              >
                Imprimir
              </Botao>
            </div>
          </div>
        </Sobreposicao>
      )}

      {vendo && ampliandoFolha && (
        <VisorDeImagem
          titulo="Folha como vai sair"
          fotoUrl={vendo.url}
          tamanho={TAMANHO_DA_FOLHA}
          alt="A folha como será impressa"
          areaUtil={AREA_UTIL_DA_FOLHA}
          aoFechar={() => setAmpliandoFolha(false)}
        />
      )}

      {painel === "imprimir" && (
        <PainelImprimir
          alvo={{
            tipo: "folha",
            folha,
            numeroDaFolha: folhas.findIndex((f) => f.id === folha.id) + 1,
            totalDeFolhas: folhas.length,
          }}
          ehAdmin={ehAdmin}
          podeCompartilhar={podeCompartilhar}
          aoFechar={fecharPainel}
          aoBaixar={() => {
            setPainel(null);
            void baixar();
          }}
          aoCompartilhar={() => {
            setPainel(null);
            void compartilhar();
          }}
          cadastro={{ tipo: "painel", aoAbrir: () => setPainel("impressoras") }}
        />
      )}

      {painel === "impressoras" && ehAdmin && <PainelDeImpressoras aoFechar={fecharPainel} />}
    </Pagina>
  );
}
